import { combineLatest, forkJoin, interval, Observable, of, zip } from 'rxjs';
import { catchError, concatMap, defaultIfEmpty, first, flatMap, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { DataStoreOptions, ItemOfflineDataFetcher, MEDIA_SUBJECTS_KEYS, OfflineDataFetcher, PushCachedValueResult, SynchroMediaUpdates } from '@skinsoft/data-store';
import { SearchResponse } from '@skinsoft/types'
import { API_ENDPOINTS, Expression, Manifestation, Work } from '@skinsoft/entity';
import { EntityDataStore } from '@skinsoft/entity-data-store';
import { searchResultSelectors, wemiSelectors } from '@skinsoft/search-data-store';
import _ from 'lodash';
import { isWemi, Wemi, WemiUtils } from '../wemi-utils';
import { ImageTransferHelper, MediaTransferHelper } from '@skinsoft/media-transfer-helper';
import { ID_STATUS_CREATED_ON_SKIN_REPORTER } from '@skinsoft/utils';

export type SearchResult = Item & Manifestation & Expression & Work;

declare var ITEM_SEARCH_TYPES: string[];

export interface Item {
    id: string;
    identificationItemSchema: {
        barCodes: {
            translatedString: {
                fr: string;
            };
        };
        identifiers: any[]
    };
    associatedMedias: any
    stateItemSchema: any
    type: string
    linkToManifestation: {
        referentManifestation: string
    },
    institutionCreation: string,
    conditioningItemSchema?: {
        actualContainer?: string[]
    }

}

export class ItemDataStore extends EntityDataStore<Item, any, SearchResult | Wemi> {
    public static getInstance() {
        if (!ItemDataStore.instance) {
            ItemDataStore.instance = new ItemDataStore();
        }

        return ItemDataStore.instance;
    }

    public static install(Vue) {
        Vue.prototype.$itemDataStore = ItemDataStore.getInstance();
    }

    public static getItemSearchResultId(itemSearchResult: SearchResult | Wemi) {
        if ("id" in itemSearchResult) {
            return searchResultSelectors.id(itemSearchResult);
        } else {
            return wemiSelectors.id(itemSearchResult);
        }
    }

    private static instance: ItemDataStore = null as any;

    constructor() {
        super('items', ITEM_SEARCH_TYPES);
    }

    public exists(id: Item['id']) {
        return this.fetchOne(id)
            .pipe(first())
            .toPromise();
    }

    public searchAll(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {}, excludedIds: string[] | null = null, onlyOnLocallyCreated: boolean = false)
        : Observable<SearchResponse<SearchResult | Wemi>> {
        return super.searchAll(queryText, sortObject, pageSize, types, page, advancedSearches, filteredIds, pathAndValueToCheck, searchEndpointOverride, options, excludedIds, onlyOnLocallyCreated)
    }
    
    public searchAllOffline(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {}, excludedIds: string[] | null = null, onlyOnLocallyCreated: boolean = false)
        : Observable<SearchResponse<SearchResult | Wemi>> {
        return super.searchAllOffline(queryText, sortObject, pageSize, types, page, advancedSearches, filteredIds, pathAndValueToCheck, searchEndpointOverride, options, excludedIds, onlyOnLocallyCreated)
                .pipe(
                    flatMap(this.addWemi),
                    map(this.mergeWemi)
                );
    }

    public addWemi(data): Observable<[any, SearchResponse<SearchResult | Wemi>]> {
        const allWemi = data.allData.map(item => {
            return WemiUtils.getWemiFromItem(item.id)
        })

        return combineLatest([forkJoin(allWemi).pipe(defaultIfEmpty(data)), of(data)])
    }

    public mergeWemi(data): SearchResponse<SearchResult | Wemi> {
        return {...data, allData: _.mergeWith(data[1].allData, data[0], (item, wemi) => {
            if(item && isWemi(item)) return item

            const workTitles = wemi.work?.descriptionMuseumWorkSchema?.workTitles;

            return {
                ...wemi,
                item,
                work: {
                    ...wemi.work,
                    indexTitle: (workTitles && workTitles.length) ? (workTitles[0]?.title) : undefined
                }
            }
        })}
    }

    private pushCachedCreationOrUpdate(dataToPush: SynchroMediaUpdates<Item>, item: Item): Observable<PushCachedValueResult> {
        this.synchronizationMap[dataToPush.id] = 'inProgress'
        return this.getWemiFromItem(
            dataToPush.id,
        )
            .pipe(
                mergeMap((async wemi => {
                    wemi = await WemiUtils.mergeWemiWithNewItem(wemi, item)
                    if(wemi.item?.associatedMedias.first && MediaTransferHelper.isLocalMedia(wemi.item?.associatedMedias.first)) {
                        _.set(wemi, 'item.associatedMedias.first', null)
                    }

                    const fixSubelement = ((object: { [key: string]: any | any[] }) => {
                        for (const [key, value] of Object.entries(object)) {
                            if (typeof value === 'object' && _.isEmpty(value)) {
                                _.set(object, key, null)
                            } else if (Array.isArray(value)) {
                                value.forEach(e => fixSubelement(e))
                                _.set(object, key, value.filter(o => !(typeof o === 'object' && o !== null && _.isEmpty(o))))
                            } else if (typeof value === 'object' && value !== null) {
                                fixSubelement(value)
                            }
                        }
                    })
                
                    fixSubelement(wemi)
                        
                    return wemi
                })),
                mergeMap(wemi => WemiUtils.saveWemi(wemi, true)),
                tap(wemi => this.synchronizationMap[dataToPush.id] = wemi.find((wemiElement: { favType: string; }) => wemiElement.favType === 'item').id),
                tap((async wemi => {
                    const imageSubject = dataToPush.mediaSubjects[MEDIA_SUBJECTS_KEYS.image]
                    let newFirstMedia: string | undefined 

                    if(imageSubject) {
                        const timer = interval(1000)
                        // tslint:disable-next-line: no-shadowed-variable
                        zip(imageSubject, timer).pipe(map(([a, _]) => a)).subscribe((replaySubjectContent) => {
                            if(replaySubjectContent.newMediaId) {
                                this.addImage(wemi.find((wemiElement: { favType: string; }) => wemiElement.favType === 'item'), replaySubjectContent as { newMediaId: string })
                                ImageTransferHelper.getInstance().addImageToReporterSubAlbum(replaySubjectContent.newMediaId)

                                ImageTransferHelper.getInstance().deleteImageInCache(replaySubjectContent.newMediaId as string)
                                if(replaySubjectContent.cacheKeys) {
                                    const localNewFirstMedia = dataToPush.cachedMedias.first

                                    for(const cacheKey of replaySubjectContent.cacheKeys) {
                                        ImageTransferHelper.getInstance().deleteImageInCache(cacheKey)

                                        // we register the id of the new first media now that it has been created (to update it later)
                                        if(localNewFirstMedia && cacheKey === localNewFirstMedia) {
                                            newFirstMedia = replaySubjectContent.newMediaId
                                        }
                                    }
                                }
                            }
                        },
                        () => {/**/},
                        async () => {
                            // we reupdate the item to correct the first media if necessary
                            if(newFirstMedia || !dataToPush.body.associatedMedias.first) {
                                const itemId = wemi.find((wemiElement: { favType: string; }) => wemiElement.favType === 'item').id
                                const fullItem = await this.fetchOne(itemId).toPromise()

                                fullItem.associatedMedias.first = newFirstMedia || null
                                if(fullItem.associatedMedias.first != null) {
                                    fullItem.associatedMedias.medias.push(newFirstMedia)
                                    fullItem.associatedMedias.medias = _.uniq(fullItem.associatedMedias.medias)
                                }

                                this.update(itemId, fullItem, undefined, true)
                            }
                        })
                    }
                })),
                first(),
                // Handle success or error if the update and emit boolean
                catchError(e => of(Error('Error during update...'))),
                concatMap(() => WemiUtils.getWemiFromItemOffline(dataToPush.id)),
                // Wait for deletion to be completed
                switchMap((async (wemi) => {
                    await WemiUtils.deleteModificationsWemi(wemi)
                    return wemi
                })),
                switchMap(
                    response => {
                        if (!(response instanceof Error)) {
                            return of({ success: true })
                        }
                        return of({ success: false });
                    }
                )
            )
    }

    protected pushCachedUpdate(dataToUpdate): Observable<PushCachedValueResult> {
        if (dataToUpdate.id && dataToUpdate.body) {
            return this.fetchOne(dataToUpdate.id).pipe(
                switchMap(item => {
                    return this.pushCachedCreationOrUpdate(dataToUpdate, item)
                })
            )
        }
        else {
            return of({ success: true });
        }
    }

    protected pushCachedCreations(dataToCreate): Observable<PushCachedValueResult> {
        if (dataToCreate.id && dataToCreate.body) {
            return this.create().pipe(
                switchMap(item => {
                    return this.pushCachedCreationOrUpdate(dataToCreate, item)
                })
            )
        }
        else {
            return of({ success: true });
        }
    }

    public formatDataForCaching(rawData) {
        return rawData.map(data => {
            if(isWemi(data)) {
                return data.item
            }
            return data
        })
    }

    public update(id: string, body: Partial<Item>, endpointOverride?: string, ignoreCachedStatus: boolean = false): Observable<Item> {
        return super.update(id, body, endpointOverride || API_ENDPOINTS[body.type as string], ignoreCachedStatus)
    }

    public getWemiFromItem(itemId: string): Observable<Wemi> {
        if (OfflineDataFetcher.isTemporaryId(itemId)) {
            return WemiUtils.getWemiFromItemOffline(itemId)
        }
        return WemiUtils.getWemiFromItem(itemId)
    }

    public create(createBody?: Partial<Item>, body?: Partial<Item>) {
        const uri = '/physical-museum-items';
        if (window.navigator.onLine || !body) {
            return this.proxy.fetch(uri, createBody, 'POST').pipe(
                concatMap(response => {
                    if (!body) {
                        return of(response);
                    }
                    // In case of a locally created entity, replacing temporary id by the one from db
                    const id: string = _.get(body, "id", '')
                    if (id && OfflineDataFetcher.isTemporaryId(id)) {
                        Object.defineProperty(body, "id", { value: response.id })
                    }
                    _.set(body, 'managementData.entryStatus', ID_STATUS_CREATED_ON_SKIN_REPORTER)
                    return this.update(response.id, _.merge(response, body));
                }),
                switchMap(({ id }) => this.fetchOne(id)),
            );
        } else { // Offline mode
            // the '_' prefix of the id identifies the id as temporary in the cache keys
            return this.createOffline(body);
        }
    }

    public addImage(item: Item, image: { newMediaId: string }) {
        return this.proxy.fetch(`/physical-museum-items/addMedia/${item.id}`,{idMedia: image.newMediaId}, 'PUT')
    }

    public fetchIfHasAccess(item: string) {
        return this.fetchOne(item).pipe(
            catchError((err) => err.status === 403 ? of(null) : err)
        )
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new ItemOfflineDataFetcher(this.sanitizedIdentifer);
    }
}
